/**
 * App configuration.
 *
 * The API key lives in `geminiKey.local.ts`, which is gitignored. This module
 * is the ONLY place that file may be imported from — everything else imports
 * from `src/config`.
 */
import { GEMINI_API_KEY as LOCAL_KEY } from './geminiKey.local';

/**
 * Where Gemini calls go, and who holds the key.
 *
 * TWO MODES, chosen at BUILD time by `VITE_GEMINI_PROXY`:
 *
 *   unset  — LOCAL DEV on this machine. The browser calls Google directly and
 *            carries the key from `geminiKey.local.ts`. Fine on localhost,
 *            where nobody else can read the bundle.
 *
 *   set    — ANYTHING REACHABLE FROM OUTSIDE (the phone, the tunnel, a real
 *            server). The browser calls that URL instead; `tools/api-server.py`
 *            holds the key and forwards. The key is NOT in the bundle: the
 *            ternary below is resolved at build time, so the import is dead
 *            code and Rollup drops it.
 *
 * Never publish a build made without the proxy — the key would ship inside the
 * JavaScript and anyone opening the page could read it. Verify with:
 *     npm run build && grep -c "<key prefix>" dist/assets/*.js    -> 0
 */
const PROXY_BASE = (import.meta.env.VITE_GEMINI_PROXY as string | undefined)?.replace(/\/+$/, '');

/** Empty in proxy builds — the client is not supposed to know the key. */
export const GEMINI_API_KEY = PROXY_BASE ? '' : LOCAL_KEY;

/** True when this build talks to the proxy rather than straight to Google. */
export const USES_GEMINI_PROXY = Boolean(PROXY_BASE);

/** Gemini model used for the first pass over the raw audio. */
export const GEMINI_MODEL = 'gemini-2.5-flash';

/** Where the app POSTs: the proxy when configured, Google directly otherwise. */
export const GEMINI_ENDPOINT = PROXY_BASE
  ? `${PROXY_BASE}/gemini`
  : `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`;

/** Header name carrying the key. Never put the key in the query string. */
export const GEMINI_API_KEY_HEADER = 'x-goog-api-key';

/**
 * Can this build reach Gemini at all? Either it has a key (local dev) or it
 * has a proxy that holds one (deployed).
 */
export const hasGeminiKey = USES_GEMINI_PROXY || (typeof LOCAL_KEY === 'string' && LOCAL_KEY.length > 0);

/* ------------------------------------------------------------------ */
/* Local helper servers                                                */
/* ------------------------------------------------------------------ */
/**
 * Two optional Python helpers, each in its OWN virtualenv (their pins are
 * mutually incompatible), started by hand from `tools/`. Neither is required:
 * when nothing answers, the first pass degrades to the legacy fallback — which
 * is what every phone does, since a phone cannot run either server.
 *
 * The ports are declared here so the TypeScript client and the Python server
 * cannot drift apart silently. They must match the defaults in
 * `tools/aligner-server.py` and `tools/whisperx-server.py`.
 *
 * 127.0.0.1 rather than localhost is deliberate: it skips the DNS lookup that
 * makes a "is the helper up?" health check take seconds to fail on some
 * machines instead of milliseconds.
 */

/** MMS forced aligner — measures word times. See `tools/aligner-server.py`. */
export const ALIGNER_PORT = 8971;
export const ALIGNER_URL = `http://127.0.0.1:${ALIGNER_PORT}`;

/** WhisperX — transcribes AND aligns. See `tools/whisperx-server.py`. */
export const WHISPERX_PORT = 8972;
export const WHISPERX_URL = `http://127.0.0.1:${WHISPERX_PORT}`;
